import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { OpenAIBaseService } from './open-ai-base.service';

export interface ValidationResult {
  isValid: boolean;
  confidence: number;
  documentType: string;
  rejectionReason?: string;
}

/**
 * Service for validating that uploaded documents are actually resumes
 * Uses GPT-4o-mini for cost-effective document classification
 */
@Injectable({
  providedIn: 'root',
})
export class DocumentValidationService extends OpenAIBaseService {
  private readonly MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB max file size
  private readonly ALLOWED_FILE_TYPE = 'application/pdf'; // Only PDF files
  private readonly pdfWorkerSrc = '/assets/pdfjs/pdf.worker.min.mjs';

  constructor(protected override http: HttpClient) {
    super();
  }

  /**
   * Validate that a text document is a resume
   * @param documentText - Extracted text from document
   * @returns Promise with validation result
   */
  async validateIsResume(documentText: string): Promise<ValidationResult> {
    if (!documentText || documentText.trim().length < 50) {
      return {
        isValid: false,
        confidence: 100,
        documentType: 'empty or too short',
        rejectionReason: 'Document is empty or too short to be a valid resume',
      };
    }

    const systemPrompt = `You are a document classifier. Your job is to determine if a document is a professional resume/CV or something else.

A valid resume typically contains:
- Contact information (name, email, phone)
- Work experience or employment history
- Education background
- Skills or competencies

Return ONLY valid JSON in this exact format:
{
  "isResume": boolean,
  "confidence": number (0-100),
  "documentType": "string",
  "reason": "string"
}

If this is NOT a resume (e.g., invoice, receipt, article, letter, bill, contract, form), set isResume to false and explain what type of document it appears to be.`;

    const userPrompt = `Analyze this document and determine if it is a professional resume/CV:

${documentText.substring(0, 3000)}

Respond with JSON only.`;

    try {
      const content = await this.makeOpenAIChatRequest(
        userPrompt,
        systemPrompt,
        'gpt-4o-mini', // Cost-effective model for classification
        0.3 // Lower temperature for consistent classification
      );

      const result = this.parseJsonResponse(content);
      
      return {
        isValid: result.isResume === true,
        confidence: result.confidence || 0,
        documentType: result.documentType || 'unknown',
        rejectionReason: result.isResume ? undefined : result.reason,
      };
    } catch (error) {
      console.error('Error validating document:', error);
      throw new Error('Failed to validate document. Please try again.');
    }
  }

  /**
   * Validate that an uploaded file is a resume (PDF only)
   * Extracts text from PDF and validates content
   * @param file - Uploaded file
   * @returns Promise with validation result
   */
  async validateResumeFile(file: File): Promise<ValidationResult> {
    // Validate file type - PDF only
    if (file.type !== this.ALLOWED_FILE_TYPE) {
      return {
        isValid: false,
        confidence: 100,
        documentType: 'unsupported file type',
        rejectionReason: `Only PDF files are supported. You uploaded: ${file.type || 'unknown file type'}`,
      };
    }

    // Validate file size
    if (file.size > this.MAX_FILE_SIZE) {
      return {
        isValid: false,
        confidence: 100,
        documentType: 'file too large',
        rejectionReason: `File size exceeds the maximum limit of ${
          this.MAX_FILE_SIZE / (1024 * 1024)
        }MB`,
      };
    }

    // Validate file size is not zero
    if (file.size === 0) {
      return {
        isValid: false,
        confidence: 100,
        documentType: 'empty file',
        rejectionReason: 'The uploaded file is empty',
      };
    }

    try {
      // Extract text from PDF
      const extractedText = await this.extractTextFromPDF(file);
      
      if (!extractedText || extractedText.trim().length < 50) {
        return {
          isValid: false,
          confidence: 90,
          documentType: 'unreadable or empty PDF',
          rejectionReason: 'Could not extract text from PDF. The file may be corrupted, image-based, or empty.',
        };
      }

      // Validate the extracted text is a resume
      return await this.validateIsResume(extractedText);
    } catch (error) {
      console.error('Error processing PDF file:', error);
      return {
        isValid: false,
        confidence: 100,
        documentType: 'processing error',
        rejectionReason: 'Failed to process PDF file. Please ensure it is a valid, readable PDF.',
      };
    }
  }

  /**
   * Extract text from PDF file
   * Uses pdf.js library for text extraction
   * @param file - PDF file
   * @returns Promise with extracted text
   */
  private async extractTextFromPDF(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = async (e) => {
        try {
          const arrayBuffer = e.target?.result as ArrayBuffer;
          
          // Dynamically import pdf.js
          const pdfjsLib = await import('pdfjs-dist');
          
          // Use local worker file to satisfy CSP in production
          pdfjsLib.GlobalWorkerOptions.workerSrc = this.pdfWorkerSrc;
          
          // Load PDF document
          const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
          
          let fullText = '';
          
          // Extract text from each page
          for (let i = 1; i <= pdf.numPages; i++) {
            const page = await pdf.getPage(i);
            const textContent = await page.getTextContent();
            const pageText = textContent.items
              .map((item: any) => item.str)
              .join(' ');
            fullText += pageText + '\n';
          }
          
          resolve(fullText.trim());
        } catch (error) {
          console.error('Error extracting text from PDF:', error);
          reject(new Error('Failed to extract text from PDF'));
        }
      };
      
      reader.onerror = () => {
        reject(new Error('Failed to read PDF file'));
      };
      
      reader.readAsArrayBuffer(file);
    });
  }

  /**
   * Get user-friendly error message for validation failure
   * @param result - Validation result
   * @returns User-friendly error message
   */
  getValidationErrorMessage(result: ValidationResult): string {
    if (result.isValid) {
      return '';
    }

    if (result.rejectionReason) {
      return result.rejectionReason;
    }

    const docType = result.documentType.toLowerCase();
    
    if (docType.includes('invoice') || docType.includes('bill')) {
      return `This appears to be ${result.documentType}, not a resume. Please upload your professional resume/CV.`;
    }
    
    if (docType.includes('article') || docType.includes('document')) {
      return `This appears to be ${result.documentType}, not a resume. Please upload a document containing your work experience, education, and skills.`;
    }
    
    return `This doesn't appear to be a resume. It looks like ${result.documentType}. Please upload a valid resume/CV.`;
  }
}

